import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import PayoutWeekdayCard from "../../components/PayoutWeekdayCard";

const resolveDisplayCustomerName = (raw) => {
  if (!raw) return "Customer";

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed || "Customer";
  }

  if (typeof raw === "object") {
    const fromName = String(raw.name || raw.fullName || "").trim();
    if (fromName) return fromName;

    const first = String(raw.firstName || "").trim();
    const last = String(raw.lastName || "").trim();
    const joined = `${first} ${last}`.trim();
    if (joined) return joined;
  }

  return "Customer";
};

const resolveReferenceDetails = (entry) => {
  const bookingNumber = String(entry?.bookingNumber || "").trim();
  const orderNumber = String(entry?.orderNumber || "").trim();
  const referenceNumber = String(entry?.referenceNumber || "").trim();

  return {
    bookingNumber,
    orderNumber,
    referenceNumber: referenceNumber || bookingNumber || orderNumber,
  };
};

const buildReferenceLabel = (entry) => {
  const { bookingNumber, orderNumber, referenceNumber } =
    resolveReferenceDetails(entry);

  const parts = [];
  if (bookingNumber) parts.push(`Booking ${bookingNumber}`);
  if (orderNumber) parts.push(`Order ${orderNumber}`);
  if (parts.length > 0) return parts.join(" · ");
  if (referenceNumber) return referenceNumber;
  return "Ref unavailable";
};

// Maps canonical PayoutLedgerEntry fields → booking-shaped dashboard objects
// so the existing render tree needs no changes.
const normalizeCanonicalForStaff = (rec) => {
  const sl = (t) =>
    ({
      booking_service: "Service Booking",
      booking_product: "Product Booking",
      order_product: "Order Product",
      booth_rent: "Booth Rent",
      package_shop_share: "Package — Shop share",
      package_session: "Package — Session",
      manual: "Manual",
    })[t] ||
    t ||
    "Earning";

  const resolveEntryLabel = (entry) => {
    const fallback = sl(entry?.sourceType);
    const rawName = String(entry?.serviceName || "").trim();

    // Canonical API can return generic "Service" fallback for product entries.
    // Prefer source-type label so product rows are not narrated as service.
    if (!rawName || rawName.toLowerCase() === "service") {
      return fallback;
    }

    return rawName;
  };

  const mapEntry = (e) => ({
    _id: e._id,
    sourceType: e.sourceType,
    serviceName: resolveEntryLabel(e),
    customerName: resolveDisplayCustomerName(e.customerName),
    bookingNumber: e.bookingNumber || null,
    orderNumber: e.orderNumber || null,
    referenceNumber: e.referenceNumber || null,
    appointmentDate: e.appointmentDate
      ? new Date(e.appointmentDate + "T12:00:00").toLocaleDateString("en-US")
      : e.createdAt
        ? new Date(e.createdAt).toLocaleDateString("en-US")
        : "\u2014",
    appointmentTime: e.appointmentTime || "",
    professionalServiceEarnings: Math.max(
      0,
      (e.netEligibleAmount || 0) - (e.tipAmount || 0),
    ),
    tipAmount: e.tipAmount || 0,
    completedAt: e.completedAt || e.createdAt,
    createdAt: e.createdAt,
    autoReleaseAt: e.heldUntil || null,
    heldUntil: e.heldUntil || null,
    payoutReleasedAt: e.eligibleAt || null,
    eligibleAt: e.eligibleAt || null,
    paidOutAt: e.paidOutAt,
  });
  return {
    held: (rec.held?.entries || []).map(mapEntry),
    released: (rec.released?.entries || []).map(mapEntry),
    history: (rec.history || []).map((h) => ({
      ...h,
      bookingCount: h.bookingCount || h.entryCount || 0,
    })),
    nextPayoutDate: rec.nextPayoutDate,
    totalPaidThisMonth: rec.totalPaidThisMonth,
  };
};

const StaffPayouts = ({ roleType = "professional" }) => {
  const navigate = useNavigate();
  const roleCode =
    roleType === "freelancer"
      ? "R4"
      : roleType === "booth-renter"
        ? "R7"
        : "R3";
  const isFreelancerLayout =
    roleType === "freelancer" || roleType === "booth-renter";
  const [loading, setLoading] = useState(true);
  const [payoutData, setPayoutData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    held: true,
    released: true,
    history: false,
  });
  const [holdHours, setHoldHours] = useState(24); // Default 24, will be fetched from platform settings
  const [usingCanonical, setUsingCanonical] = useState(false);
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [instantPayoutData, setInstantPayoutData] = useState(null);
  const [loadingInstantPayout, setLoadingInstantPayout] = useState(false);
  const [processingInstantPayout, setProcessingInstantPayout] = useState(false);
  const [instantPayoutError, setInstantPayoutError] = useState("");
  const [instantPayoutSuccess, setInstantPayoutSuccess] = useState("");
  const [instantPayoutVisible, setInstantPayoutVisible] = useState(false);
  const [instantPayoutNeedsBankSetup, setInstantPayoutNeedsBankSetup] =
    useState(false);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("ogestyle_token");
    navigate("/login");
  }, [navigate]);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      return null;
    }
    return baseURL;
  }, []);

  // Fetch payout reconciliation data — canonical-aware
  const fetchPayoutData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) {
        navigate("/login");
        return;
      }

      const apiBaseUrl = getApiBaseUrl();

      // Fetch platform settings for hold hours
      try {
        const settingsResponse = await fetch(
          `${apiBaseUrl}/api/admin/settings/public`,
          { headers },
        );
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          setHoldHours(settingsData.payoutSettings?.earningsHoldHours || 72);
        }
      } catch (_) {}

      setUsingCanonical(true);

      const res = await fetch(
        `${apiBaseUrl}/api/payout-ledger/staff/reconciliation?role=${roleCode}`,
        { headers },
      );
      if (!res.ok) {
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || "Failed to fetch payout reconciliation");
      }
      setPayoutData(normalizeCanonicalForStaff(await res.json()));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, handleUnauthorized, roleCode]);

  useEffect(() => {
    fetchPayoutData();
  }, [fetchPayoutData]);

  const fetchStripeConnectStatus = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/staff/connect/status?role=${roleCode}`,
        { headers },
      );
      if (response.ok) {
        setStripeConnectStatus(await response.json());
      }
    } catch (error) {
    }
  }, [getAuthHeaders, getApiBaseUrl, roleCode]);

  useEffect(() => {
    fetchStripeConnectStatus();
  }, [fetchStripeConnectStatus]);

  const fetchInstantPayoutAvailability = useCallback(async () => {
    if (!usingCanonical) {
      setInstantPayoutData(null);
      setInstantPayoutError("");
      setInstantPayoutVisible(false);
      setInstantPayoutNeedsBankSetup(false);
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;

    setLoadingInstantPayout(true);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/payout-ledger/staff/instant-payout/available?role=${roleCode}`,
        { headers },
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = String(json.message || "");
        const needsBankSetup =
          response.status === 400 &&
          message.toLowerCase().includes("no bank account connected");

        setInstantPayoutNeedsBankSetup(needsBankSetup);
        if (needsBankSetup) {
          throw new Error(
            "Instant payout is unavailable until your Stripe bank account is connected.",
          );
        }
        throw new Error(json.message || "Failed to load instant payout data");
      }

      if (json.instantPayoutEnabled === false || json.isEnabled === false) {
        setInstantPayoutData(null);
        setInstantPayoutVisible(false);
        setInstantPayoutNeedsBankSetup(false);
        setInstantPayoutError("");
        return;
      }

      setInstantPayoutData(json);
      setInstantPayoutVisible(true);
      setInstantPayoutNeedsBankSetup(false);
      setInstantPayoutError("");
    } catch (error) {
      setInstantPayoutData(null);
      setInstantPayoutVisible(false);
      if (
        String(error?.message || "")
          .toLowerCase()
          .includes("bank account")
      ) {
        setInstantPayoutNeedsBankSetup(true);
      }
      setInstantPayoutError(
        error.message || "Failed to load instant payout data",
      );
    } finally {
      setLoadingInstantPayout(false);
    }
  }, [
    usingCanonical,
    getAuthHeaders,
    getApiBaseUrl,
    handleUnauthorized,
    roleCode,
  ]);

  useEffect(() => {
    fetchInstantPayoutAvailability();
  }, [fetchInstantPayoutAvailability]);

  const handleRequestInstantPayout = async () => {
    if (!instantPayoutData?.canRequestPayout) return;

    const confirmed = window.confirm(
      `You will receive $${instantPayoutData.netAmount.toFixed(2)} after a ${instantPayoutData.instantFeePercent}% fee ($${instantPayoutData.instantFee.toFixed(2)}). Proceed with instant payout?`,
    );
    if (!confirmed) return;

    const headers = getAuthHeaders();
    if (!headers) return;
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;

    setProcessingInstantPayout(true);
    setInstantPayoutError("");
    setInstantPayoutSuccess("");

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/payout-ledger/staff/instant-payout/request?role=${roleCode}`,
        {
          method: "POST",
          headers,
        },
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || "Failed to process instant payout");
      }

      setInstantPayoutSuccess(
        `Instant payout of $${Number(json.netAmount || 0).toFixed(2)} processed successfully.`,
      );
      await fetchPayoutData();
      await fetchInstantPayoutAvailability();
    } catch (error) {
      setInstantPayoutError(
        error.message || "Failed to process instant payout",
      );
    } finally {
      setProcessingInstantPayout(false);
    }
  };

  const handleConnectStripe = async () => {
    setLoadingConnect(true);
    const headers = getAuthHeaders();
    if (!headers) {
      setLoadingConnect(false);
      return;
    }
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
      setLoadingConnect(false);
      return;
    }

    const parseJsonSafely = async (response) => {
      try {
        return await response.json();
      } catch (_) {
        return {};
      }
    };

    try {
      // Resume existing onboarding first.
      let linkRes = await fetch(
        `${apiBaseUrl}/api/staff/connect/onboarding-link?role=${roleCode}`,
        { method: "POST", headers },
      );

      // If no account exists yet, create one then request onboarding link again.
      if (!linkRes.ok) {
        const linkErr = await parseJsonSafely(linkRes);
        if (linkErr?.error === "no_account" || linkErr?.requiresAccountCreation) {
          const createRes = await fetch(
            `${apiBaseUrl}/api/staff/connect/create-account?role=${roleCode}`,
            { method: "POST", headers },
          );

          if (!createRes.ok) {
            const createErr = await parseJsonSafely(createRes);
            throw new Error(createErr?.message || "Failed to create Stripe Connect account");
          }

          linkRes = await fetch(
            `${apiBaseUrl}/api/staff/connect/onboarding-link?role=${roleCode}`,
            { method: "POST", headers },
          );
        }
      }

      if (!linkRes.ok) throw new Error("Failed to get onboarding link");
      const linkData = await linkRes.json();
      window.location.href = linkData.url;
    } catch (error) {
      alert(error.message || "Failed to connect to Stripe. Please try again.");
      setLoadingConnect(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return;
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/staff/connect/login-link?role=${roleCode}`,
        { method: "POST", headers },
      );
      if (!response.ok) throw new Error("Failed to get login link");
      const data = await response.json();
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      alert("Failed to open Stripe Dashboard. Please try again.");
    }
  };

  // Toggle section expand/collapse
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Format countdown timer
  const formatCountdown = (releaseDate) => {
    const now = new Date();
    const release = new Date(releaseDate);
    const diff = release - now;

    if (diff <= 0) return "Releasing soon...";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  // Calculate progress percentage for hold period
  const getHoldProgress = (completedAt, releaseDate) => {
    const completed = new Date(completedAt);
    const release = new Date(releaseDate);
    const now = new Date();
    const total = release - completed;
    const elapsed = now - completed;
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!payoutData) return;

    const allBookings = [
      ...(payoutData.held || []),
      ...(payoutData.released || []),
      ...(payoutData.history || []).flatMap((payout) => payout.bookings || []),
    ];

    const csvRows = [
      [
        "Date",
        "Customer",
        "Service",
        "Service Revenue",
        "Tips",
        "Total Earnings",
        "Status",
        "Payout Date",
      ].join(","),
    ];

    allBookings.forEach((booking) => {
      const row = [
        booking.appointmentDate,
        `"${booking.customerName || "Unknown"}"`,
        `"${booking.serviceName || "Unknown Service"}"`,
        `$${(booking.professionalServiceEarnings || 0).toFixed(2)}`,
        `$${(booking.tipAmount || 0).toFixed(2)}`,
        `$${(
          (booking.professionalServiceEarnings || 0) + (booking.tipAmount || 0)
        ).toFixed(2)}`,
        booking.canonicalStatus || booking.status || "unknown",
        booking.payoutDate || booking.autoReleaseAt || "Pending",
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `payouts_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const renderInLayout = (content) => {
    if (isFreelancerLayout) {
      return <FreelancerSidebarLayout>{content}</FreelancerSidebarLayout>;
    }
    return (
      <StaffSidebarLayout roleType={roleType}>{content}</StaffSidebarLayout>
    );
  };

  if (loading) {
    return renderInLayout(
      <div style={{ padding: "20px" }}>
        <h2>💰 Payouts & Reconciliation</h2>

        {/* Payout Weekday Card - Always visible */}
        <PayoutWeekdayCard beneficiaryType="staff" compact={true} />

        <p>Loading payout data...</p>
      </div>,
    );
  }

  if (!payoutData) {
    return renderInLayout(
      <div style={{ padding: "20px" }}>
        <h2>💰 Payouts & Reconciliation</h2>

        {/* Payout Weekday Card - Always visible */}
        <PayoutWeekdayCard beneficiaryType="staff" compact={true} />

        <p>Unable to load payout data. Please try again.</p>
      </div>,
    );
  }

  const heldBookings = payoutData.held || [];
  const releasedBookings = payoutData.released || [];
  const payoutHistory = payoutData.history || [];
  const now = new Date();
  const futureLockedBookings = heldBookings.filter((booking) => {
    const releaseAt = booking.autoReleaseAt || booking.heldUntil;
    if (!releaseAt) return false;
    const releaseDate = new Date(releaseAt);
    return !Number.isNaN(releaseDate.getTime()) && releaseDate > now;
  });

  const heldTotal = heldBookings.reduce(
    (sum, b) => sum + (b.professionalServiceEarnings || 0) + (b.tipAmount || 0),
    0,
  );
  const releasedTotal = releasedBookings.reduce(
    (sum, b) => sum + (b.professionalServiceEarnings || 0) + (b.tipAmount || 0),
    0,
  );
  const futureLockedTotal = futureLockedBookings.reduce(
    (sum, b) => sum + (b.professionalServiceEarnings || 0) + (b.tipAmount || 0),
    0,
  );
  const paidThisMonth =
    payoutData.totalPaidThisMonth ??
    (payoutHistory
      .filter((p) => {
        const payoutDate = new Date(p.payoutDate);
        const now = new Date();
        return (
          payoutDate.getMonth() === now.getMonth() &&
          payoutDate.getFullYear() === now.getFullYear()
        );
      })
      .reduce((sum, p) => sum + (p.amount || 0), 0) ||
      0);

  return renderInLayout(
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>💰 Payouts & Reconciliation</h2>
          {usingCanonical && (
            <span
              style={{
                display: "inline-block",
                marginTop: "6px",
                padding: "2px 10px",
                background: "#dcfce7",
                color: "#166534",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              ✓ Canonical Ledger
            </span>
          )}
        </div>
        <button
          onClick={exportToCSV}
          style={{
            padding: "10px 20px",
            background: "#10b981",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          📥 Export CSV
        </button>
      </div>

      {/* Payout Weekday Card */}
      <PayoutWeekdayCard beneficiaryType="staff" compact={true} />

      {/* Bank Account for Payouts */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🏦</span> Bank Account for Payouts
        </h2>

        {stripeConnectStatus?.connected &&
        stripeConnectStatus?.payoutsEnabled ? (
          <div>
            <div
              style={{
                padding: "16px",
                background: "#ecfdf5",
                border: "1px solid #10b981",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span style={{ fontSize: "20px" }}>✅</span>
                  <span style={{ fontWeight: "600", color: "#065f46" }}>
                    Stripe Account Connected
                  </span>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    onClick={handleOpenStripeDashboard}
                    style={{
                      padding: "6px 12px",
                      background: "#635BFF",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      cursor: "pointer",
                      fontWeight: "500",
                    }}
                  >
                    🔗 View Stripe Dashboard
                  </button>
                  <button
                    onClick={handleConnectStripe}
                    disabled={loadingConnect}
                    style={{
                      padding: "6px 12px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      cursor: loadingConnect ? "not-allowed" : "pointer",
                      fontWeight: "500",
                      opacity: loadingConnect ? 0.6 : 1,
                    }}
                  >
                    {loadingConnect ? "Loading..." : "Update Account"}
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#047857",
                      marginBottom: "4px",
                    }}
                  >
                    Stripe Account ID
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#065f46",
                      fontFamily: "monospace",
                    }}
                  >
                    {stripeConnectStatus.accountId
                      ? `${stripeConnectStatus.accountId.substring(0, 12)}...`
                      : "N/A"}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#047857",
                      marginBottom: "4px",
                    }}
                  >
                    Status
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#065f46",
                    }}
                  >
                    ✓ Payouts Enabled
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                padding: "12px",
                background: "#eff6ff",
                border: "1px solid #3b82f6",
                borderRadius: "8px",
              }}
            >
              <p style={{ fontSize: "13px", color: "#1e40af", margin: 0 }}>
                💰 Your earnings are automatically transferred to your Stripe
                account. You can manage bank account details and view transfer
                history in your Stripe Dashboard.
              </p>
            </div>
          </div>
        ) : stripeConnectStatus?.connected &&
          !stripeConnectStatus?.payoutsEnabled ? (
          <div>
            <div
              style={{
                padding: "16px",
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "20px" }}>⏳</span>
                <span style={{ fontWeight: "600", color: "#92400e" }}>
                  Account Setup Incomplete
                </span>
              </div>
              <p
                style={{
                  fontSize: "14px",
                  color: "#92400e",
                  margin: "0 0 12px 0",
                }}
              >
                Your Stripe account is connected but requires additional
                information to enable payouts.
              </p>
              {stripeConnectStatus?.requirements &&
                stripeConnectStatus.requirements.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#92400e",
                        fontWeight: "600",
                        marginBottom: "6px",
                      }}
                    >
                      Required Information:
                    </div>
                    <ul
                      style={{
                        fontSize: "13px",
                        color: "#92400e",
                        margin: 0,
                        paddingLeft: "20px",
                      }}
                    >
                      {stripeConnectStatus.requirements.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              <button
                onClick={handleConnectStripe}
                disabled={loadingConnect}
                style={{
                  background: "#f59e0b",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loadingConnect ? "not-allowed" : "pointer",
                  opacity: loadingConnect ? 0.6 : 1,
                }}
              >
                {loadingConnect ? "Loading..." : "Resume Stripe Onboarding"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div
              style={{
                padding: "16px",
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <p style={{ fontSize: "14px", color: "#92400e", margin: 0 }}>
                ⚠️ Connect Your Bank Account via Stripe
              </p>
            </div>
            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "16px",
                lineHeight: "1.6",
              }}
            >
              <strong>Secure Bank Account Setup via Stripe:</strong>
              <br />
              • Stripe will collect your bank account details securely
              <br />
              • We never see or store your banking information
              <br />
              • You'll get your own Stripe Dashboard to manage payouts
              <br />• Automatic transfers to your bank account (frequency set by
              platform)
            </p>
            <button
              onClick={handleConnectStripe}
              disabled={loadingConnect}
              style={{
                background: "#635BFF",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: loadingConnect ? "not-allowed" : "pointer",
                opacity: loadingConnect ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>🔒</span>
              {loadingConnect
                ? "Loading..."
                : "Connect Bank Account via Stripe"}
            </button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        {/* Held */}
        <div
          style={{
            padding: "20px",
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            borderRadius: "12px",
            color: "white",
            boxShadow: "0 4px 6px rgba(245, 158, 11, 0.3)",
          }}
        >
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            💰 HELD
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}>
            ${heldTotal.toFixed(2)}
          </div>
          <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
            {heldBookings.length} booking{heldBookings.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Released */}
        <div
          style={{
            padding: "20px",
            background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
            borderRadius: "12px",
            color: "white",
            boxShadow: "0 4px 6px rgba(99, 102, 241, 0.3)",
          }}
        >
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            ⏳ FUTURE PAYMENTS
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}>
            ${futureLockedTotal.toFixed(2)}
          </div>
          <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
            {futureLockedBookings.length} locked until release date
          </div>
        </div>

        {/* Released */}
        <div
          style={{
            padding: "20px",
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            borderRadius: "12px",
            color: "white",
            boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
          }}
        >
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            📤 RELEASED (Queued for Transfer)
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}>
            ${releasedTotal.toFixed(2)}
          </div>
          <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
            {payoutData.nextPayoutDate
              ? `Next: ${new Date(payoutData.nextPayoutDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`
              : "Awaiting next batch"}
          </div>
        </div>

        {/* Paid This Month */}
        <div
          style={{
            padding: "20px",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            borderRadius: "12px",
            color: "white",
            boxShadow: "0 4px 6px rgba(16, 185, 129, 0.3)",
          }}
        >
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            ✅ PAID THIS MONTH
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", lineHeight: 1 }}>
            ${paidThisMonth.toFixed(2)}
          </div>
          <div style={{ fontSize: "12px", marginTop: "8px", opacity: 0.9 }}>
            {
              payoutHistory.filter((p) => {
                const payoutDate = new Date(p.payoutDate);
                const now = new Date();
                return (
                  payoutDate.getMonth() === now.getMonth() &&
                  payoutDate.getFullYear() === now.getFullYear()
                );
              }).length
            }{" "}
            payout(s)
          </div>
        </div>
      </div>

      {usingCanonical && instantPayoutNeedsBankSetup && (
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#92400e",
                  marginBottom: "4px",
                }}
              >
                ⚠ Instant payout needs bank setup
              </div>
              <div style={{ fontSize: "13px", color: "#92400e" }}>
                Connect your Stripe bank account to enable instant payout.
              </div>
            </div>
            <button
              onClick={handleConnectStripe}
              disabled={loadingConnect}
              style={{
                background: "#0b63ce",
                color: "white",
                border: "none",
                padding: "10px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: loadingConnect ? "not-allowed" : "pointer",
                opacity: loadingConnect ? 0.6 : 1,
              }}
            >
              {loadingConnect ? "Loading..." : "Connect Bank Account"}
            </button>
          </div>
        </div>
      )}

      {usingCanonical && instantPayoutVisible && (
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "24px",
            border: "1px solid #dbeafe",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
              marginBottom: "12px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                ⚡ Instant Payout
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Pull currently released professional earnings forward for an
                instant payout fee.
              </div>
            </div>
            <button
              onClick={handleRequestInstantPayout}
              disabled={
                loadingInstantPayout ||
                processingInstantPayout ||
                !instantPayoutData?.canRequestPayout
              }
              style={{
                padding: "12px 18px",
                background:
                  instantPayoutData?.canRequestPayout &&
                  !processingInstantPayout
                    ? "#0f766e"
                    : "#9ca3af",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor:
                  instantPayoutData?.canRequestPayout &&
                  !processingInstantPayout
                    ? "pointer"
                    : "not-allowed",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              {processingInstantPayout
                ? "Processing..."
                : "Request Instant Payout"}
            </button>
          </div>

          {loadingInstantPayout ? (
            <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
              Checking instant payout availability...
            </p>
          ) : (
            <>
              {instantPayoutData && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      padding: "12px",
                      background: "#eff6ff",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#1d4ed8",
                        marginBottom: "4px",
                      }}
                    >
                      Available Now
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: "700",
                        color: "#1e3a8a",
                      }}
                    >
                      ${Number(instantPayoutData.grossAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "12px",
                      background: "#fff7ed",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#c2410c",
                        marginBottom: "4px",
                      }}
                    >
                      Instant Fee
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: "700",
                        color: "#9a3412",
                      }}
                    >
                      ${Number(instantPayoutData.instantFee || 0).toFixed(2)}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "12px",
                      background: "#ecfdf5",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#047857",
                        marginBottom: "4px",
                      }}
                    >
                      Net Deposit
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: "700",
                        color: "#065f46",
                      }}
                    >
                      ${Number(instantPayoutData.netAmount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              {instantPayoutData?.processingCount > 0 && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#f8fafc",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#475569",
                    marginBottom: "12px",
                  }}
                >
                  ${Number(instantPayoutData.processingAmount || 0).toFixed(2)}{" "}
                  across {instantPayoutData.processingCount} released booking
                  {instantPayoutData.processingCount !== 1 ? "s are" : " is"}{" "}
                  already processing for the next scheduled payout and cannot be
                  pulled into an instant payout.
                </div>
              )}

              {instantPayoutData?.reason && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#475569",
                    marginBottom:
                      instantPayoutError || instantPayoutSuccess ? "12px" : 0,
                  }}
                >
                  {instantPayoutData.reason}
                </div>
              )}

              {instantPayoutData?.nextEligiblePayoutDate &&
                !instantPayoutData?.canRequestPayout && (
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#1d4ed8",
                      marginTop: "12px",
                    }}
                  >
                    Next eligible payout window:{" "}
                    {instantPayoutData.nextEligiblePayoutDate}
                  </div>
                )}
            </>
          )}

          {instantPayoutError && (
            <div
              style={{
                padding: "10px 12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#b91c1c",
                marginTop: "12px",
              }}
            >
              {instantPayoutError}
            </div>
          )}

          {instantPayoutSuccess && (
            <div
              style={{
                padding: "10px 12px",
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#065f46",
                marginTop: "12px",
              }}
            >
              {instantPayoutSuccess}
            </div>
          )}
        </div>
      )}

      {/* Held Bookings Section */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: "16px",
        }}
      >
        <div
          onClick={() => toggleSection("held")}
          style={{
            padding: "16px 20px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: expandedSections.held ? "1px solid #e5e7eb" : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px" }}>🔒</span>
            <div>
              <div style={{ fontWeight: "600", fontSize: "16px" }}>Held</div>
              <div style={{ fontSize: "13px", color: "#6b7280" }}>
                {heldBookings.length} booking
                {heldBookings.length !== 1 ? "s" : ""} · ${heldTotal.toFixed(2)}
              </div>
            </div>
          </div>
          <span style={{ fontSize: "18px", color: "#6b7280" }}>
            {expandedSections.held ? "▼" : "▶"}
          </span>
        </div>

        {expandedSections.held && (
          <div style={{ padding: "20px" }}>
            {heldBookings.length === 0 ? (
              <p style={{ color: "#9ca3af", textAlign: "center" }}>
                No bookings in hold period
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {heldBookings.map((booking) => {
                  const earnings =
                    (booking.professionalServiceEarnings || 0) +
                    (booking.tipAmount || 0);
                  const progress = booking.autoReleaseAt
                    ? getHoldProgress(booking.completedAt, booking.autoReleaseAt)
                    : 0;

                  return (
                    <div
                      key={booking._id}
                      style={{
                        padding: "16px",
                        background: "#fef3c7",
                        border: "2px solid #f59e0b",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "12px",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "15px",
                              marginBottom: "4px",
                            }}
                          >
                            {booking.serviceName}
                          </div>
                          <div style={{ fontSize: "13px", color: "#78350f" }}>
                            {resolveDisplayCustomerName(booking.customerName)} · {booking.appointmentDate}{" "}
                            {booking.appointmentTime}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#92400e",
                              marginTop: "4px",
                            }}
                          >
                            Ref: {buildReferenceLabel(booking)}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              fontWeight: "bold",
                              fontSize: "18px",
                              color: "#92400e",
                            }}
                          >
                            ${earnings.toFixed(2)}
                          </div>
                          <div style={{ fontSize: "11px", color: "#78350f" }}>
                            $
                            {(booking.professionalServiceEarnings || 0).toFixed(
                              2,
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div style={{ marginBottom: "8px" }}>
                        {booking.autoReleaseAt ? (
                          <div
                            style={{
                              height: "8px",
                              background: "#fde68a",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                background: "#f59e0b",
                                width: `${progress}%`,
                                transition: "width 0.3s",
                              }}
                            />
                          </div>
                        ) : (
                          <div style={{ fontSize: "12px", color: "#78350f" }}>
                            Release timing pending
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#92400e",
                        }}
                      >
                        Payout status: {booking.paidOutAt ? "Paid" : "Pending"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Released Bookings Section */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: "16px",
        }}
      >
        <div
          onClick={() => toggleSection("released")}
          style={{
            padding: "16px 20px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: expandedSections.released
              ? "1px solid #e5e7eb"
              : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px" }}>📤</span>
            <div>
              <div style={{ fontWeight: "600", fontSize: "16px" }}>
                Released (Queued for Transfer)
              </div>
              <div style={{ fontSize: "13px", color: "#6b7280" }}>
                {releasedBookings.length} booking
                {releasedBookings.length !== 1 ? "s" : ""} · $
                {releasedTotal.toFixed(2)}
              </div>
            </div>
          </div>
          <span style={{ fontSize: "18px", color: "#6b7280" }}>
            {expandedSections.released ? "▼" : "▶"}
          </span>
        </div>

        {expandedSections.released && (
          <div style={{ padding: "20px" }}>
            {releasedBookings.length === 0 ? (
              <p style={{ color: "#9ca3af", textAlign: "center" }}>
                No bookings ready for payout
              </p>
            ) : (
              <>
                <div
                  style={{
                    padding: "12px",
                    background: "#dbeafe",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    fontSize: "14px",
                    color: "#1e40af",
                  }}
                >
                  💡 These earnings will be included in your next weekly payout
                  on{" "}
                  <strong>
                    {payoutData.nextPayoutDate
                      ? `${new Date(payoutData.nextPayoutDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })} at 2:00 AM CT`
                      : "Next payout day at 2:00 AM CT"}
                  </strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {releasedBookings.map((booking) => {
                    const earnings =
                      (booking.professionalServiceEarnings || 0) +
                      (booking.tipAmount || 0);

                    return (
                      <div
                        key={booking._id}
                        style={{
                          padding: "16px",
                          background: "#f0f9ff",
                          border: "2px solid #3b82f6",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "15px",
                                marginBottom: "4px",
                              }}
                            >
                              ✅ {booking.serviceName}
                            </div>
                            <div style={{ fontSize: "13px", color: "#1e40af" }}>
                              {resolveDisplayCustomerName(booking.customerName)} · {booking.appointmentDate}{" "}
                              {booking.appointmentTime}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#1d4ed8",
                                marginTop: "4px",
                              }}
                            >
                              Ref: {buildReferenceLabel(booking)}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                marginTop: "4px",
                              }}
                            >
                              Released:{" "}
                              {booking.payoutReleasedAt
                                ? new Date(booking.payoutReleasedAt).toLocaleString()
                                : "—"}
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div
                              style={{
                                fontWeight: "bold",
                                fontSize: "18px",
                                color: "#1e40af",
                              }}
                            >
                              ${earnings.toFixed(2)}
                            </div>
                            <div style={{ fontSize: "11px", color: "#6b7280" }}>
                              $
                              {(
                                booking.professionalServiceEarnings || 0
                              ).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Payout History Section */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div
          onClick={() => toggleSection("history")}
          style={{
            padding: "16px 20px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: expandedSections.history
              ? "1px solid #e5e7eb"
              : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px" }}>📜</span>
            <div>
              <div style={{ fontWeight: "600", fontSize: "16px" }}>
                Payout History
              </div>
              <div style={{ fontSize: "13px", color: "#6b7280" }}>
                {payoutHistory.length} completed payout
                {payoutHistory.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <span style={{ fontSize: "18px", color: "#6b7280" }}>
            {expandedSections.history ? "▼" : "▶"}
          </span>
        </div>

        {expandedSections.history && (
          <div style={{ padding: "20px" }}>
            {payoutHistory.length === 0 ? (
              <p style={{ color: "#9ca3af", textAlign: "center" }}>
                No payout history yet
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {payoutHistory.map((payout, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "16px",
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "15px",
                            marginBottom: "4px",
                          }}
                        >
                          {new Date(payout.payoutDate).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {payout.bookingCount || 0} booking
                          {payout.bookingCount !== 1 ? "s" : ""}
                          {payout.stripeTransferId && (
                            <span style={{ marginLeft: "8px" }}>
                              · Transfer:{" "}
                              {payout.stripeTransferId.substring(0, 20)}...
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "18px",
                          color: "#059669",
                        }}
                      >
                        ${(payout.amount || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
  );
};

export default StaffPayouts;
